/**
 * MSW handlers for the Payroll Overview demo.
 *
 * These intercept the real `api/payroll/v1/` requests and answer them with the fixtures, so
 * the demo drives the actual query hooks, request layer and URLs — not a second code path that can
 * agree with the design while the wiring is wrong. Swapping to a live backend is turning MSW off.
 *
 * A separate JSON server was the alternative and would have cost a new dependency, a second
 * process and its own CORS and auth story, to prove less.
 */

// msw is a devDependency but this file is only used by Storybook, which includes devDependencies in its build
// eslint-disable-next-line import/no-extraneous-dependencies
import { http, HttpResponse, delay } from 'msw';

import providerUnavailableResponse from 'modules/Payroll/common/demo/providerUnavailable';
import {
    BLOCKED_OVERVIEW_CORE,
    MOCK_ACTIVITY,
    MOCK_ACTIVITY_WITH_PENDING,
    MOCK_ALERTS,
    MOCK_PAY_SCHEDULES,
    MOCK_FUNDING,
    MOCK_OVERVIEW_CORE,
    MOCK_YTD,
    UNCONNECTED_FUNDING,
} from 'services/payroll/queries/usePayrollOverview/usePayrollOverview.mocks';
import { PayrollYtd } from 'services/payroll/queries/usePayrollOverview/usePayrollOverview.interface';
import { demoPaystubPdf } from 'services/payroll/mutations/useEmployeePaystub/useEmployeePaystub.mocks';
import { buildOAuthResponse } from 'tests/storybook/handlers/oauth';

const OVERVIEW_URL = '*/api/payroll/v1/overview/';
const YTD_URL = '*/api/payroll/v1/ytd/';
const FUNDING_URL = '*/api/payroll/v1/funding/';
const PAYROLLS_URL = '*/api/payroll/v1/payrolls/';
const ALERTS_URL = '*/api/payroll/v1/alerts/';
const PAY_SCHEDULES_URL = '*/api/payroll/v1/pay-schedules/';

/**
 * Year-to-date refreshes its inputs from the provider before aggregating, so it is the slowest
 * section by design. The demo reproduces that rather than resolving everything at once — the
 * progressive render is the thing worth looking at.
 */
const YTD_LATENCY_MS = 1_200;

const overviewHandler = http.get(OVERVIEW_URL, () => HttpResponse.json(MOCK_OVERVIEW_CORE));

const ytdHandler = http.get(YTD_URL, async () => {
    await delay(YTD_LATENCY_MS);

    return HttpResponse.json(MOCK_YTD);
});

const fundingHandler = http.get(FUNDING_URL, () => HttpResponse.json(MOCK_FUNDING));

/** Paginates a fixture the way the server does, so the pagination control is exercised. */
const buildPayrollsHandler = (page: typeof MOCK_ACTIVITY) =>
    http.get(PAYROLLS_URL, ({ request }) => {
        const url = new URL(request.url);
        const limit = Number(url.searchParams.get('limit') ?? page.results.length);
        const offset = Number(url.searchParams.get('offset') ?? 0);

        return HttpResponse.json({ results: page.results.slice(offset, offset + limit), count: page.count });
    });

const payrollsHandler = buildPayrollsHandler(MOCK_ACTIVITY);

/** A second schedule with three runs late and three to come, so both lists stack. */
export const pendingRunsPayrollsHandler = buildPayrollsHandler(MOCK_ACTIVITY_WITH_PENDING);

/**
 * Gusto connected. Storybook's global default is disconnected, which drops every write and hides
 * the run buttons a story is meant to show. Overrides that default by its key, `oauth/getConnections`.
 */
export const connectedGustoHandler = http.get('*/oauth/', () =>
    HttpResponse.json(buildOAuthResponse({ gusto: { connected: true } }))
);

const paySchedulesHandler = http.get(PAY_SCHEDULES_URL, () => HttpResponse.json(MOCK_PAY_SCHEDULES));

/**
 * Only requested once the core reports a blocking count, so the default fixture reports none and
 * this handler is never hit on the ordinary demo. It exists so the blocked demo drives the real
 * URL rather than a prop.
 */
const alertsHandler = http.get(ALERTS_URL, () => HttpResponse.json(MOCK_ALERTS));

/**
 * The blocked company: the banner over the next-run-with-auto-payroll card, which is what the design draws.
 * Overrides the core so `blocking_alert_count` is what enables the alerts call.
 */
export const blockedOverviewHandler = http.get(OVERVIEW_URL, () => HttpResponse.json(BLOCKED_OVERVIEW_CORE));

/** `/alerts/` answering 501 while the count is non-zero. */
export const unimplementedAlertsHandler = http.get(ALERTS_URL, () =>
    HttpResponse.json(
        { error: { code: 'NOT_IMPLEMENTED', details: { operation: 'alerts_list', ships_in: 'M1' } } },
        { status: 501 }
    )
);

/**
 * A member with a long history, so the pagination control renders and the handler's `offset`
 * slicing is actually hit. The fixture only holds one page, so later pages repeat it — the
 * point is the request round trip, not the rows.
 */
export const buildLongHistoryHandler = (count: number) =>
    http.get(PAYROLLS_URL, ({ request }) => {
        const limit = Number(new URL(request.url).searchParams.get('limit') ?? MOCK_ACTIVITY.results.length);

        return HttpResponse.json({ results: MOCK_ACTIVITY.results.slice(0, limit), count });
    });

/** A section that fails while the rest of the page renders. */
export const buildFailingSectionHandler = (url: string) => http.get(url, () => providerUnavailableResponse());

export const SECTION_URLS = {
    overview: OVERVIEW_URL,
    ytd: YTD_URL,
    funding: FUNDING_URL,
    payrolls: PAYROLLS_URL,
    alerts: ALERTS_URL,
    paySchedules: PAY_SCHEDULES_URL,
} as const;

/**
 * Payroll debits an account the member has never connected to us. The page grows a prompt naming
 * the last four, and the card falls back to a generic mark with no account type — the two halves
 * of that state, which only render together on the real page.
 */
export const unconnectedFundingHandler = http.get(FUNDING_URL, () => HttpResponse.json(UNCONNECTED_FUNDING));

/** A member who has never run payroll: the tiles are genuinely zero, not failed. */
export const emptyYtdHandler = http.get(YTD_URL, () => {
    const empty: PayrollYtd = {
        year: MOCK_YTD.year,
        salary_paid: '0.00',
        contractor_pay: '0.00',
        taxes_paid: '0.00',
        benefits_paid: '0.00',
        as_of: null,
    };

    return HttpResponse.json(empty);
});

const DOWNLOAD_URL_PATTERN = '*/api/payroll/v1/documents/:documentUuid/download-url/';
// Any absolute url MSW can also intercept — standing in for the external signed-storage host a
// real signed url would point at. Matches Documents/demo/demoHandlers.ts.
const DEMO_DOCUMENT_FILE_URL = 'https://fixtures.collective.demo/payroll-documents/demo-document.pdf';

const downloadUrlHandler = http.get(DOWNLOAD_URL_PATTERN, () =>
    HttpResponse.json({
        url: DEMO_DOCUMENT_FILE_URL,
        filename: 'demo-document.pdf',
        expires_at: '2026-12-31T00:00:00Z',
    })
);

/** A signed url the backend refuses to issue. */
export const failingDownloadUrlHandler = http.get(DOWNLOAD_URL_PATTERN, () =>
    HttpResponse.json({ error: { code: 'DOCUMENT_UNAVAILABLE' } }, { status: 502 })
);

const documentFileHandler = http.get(DEMO_DOCUMENT_FILE_URL, () =>
    HttpResponse.arrayBuffer(demoPaystubPdf().buffer as ArrayBuffer, { headers: { 'Content-Type': 'application/pdf' } })
);

/** The signed url resolves, then the file itself 404s — the second-hop failure. */
export const failingDocumentFileHandler = http.get(DEMO_DOCUMENT_FILE_URL, () =>
    HttpResponse.text('', { status: 404 })
);

/** A document type `DocumentRenderer` cannot display — exercises the auto-download fallback. */
export const unsupportedDocumentFileHandler = http.get(DEMO_DOCUMENT_FILE_URL, () =>
    HttpResponse.arrayBuffer(new ArrayBuffer(8), { headers: { 'Content-Type': 'application/zip' } })
);

/**
 * Keyed by endpoint so a story overrides one section by name — `{ ...allDemoHandlers, ytd: ... }`
 * — rather than relying on where it sits in an array.
 */
const allDemoHandlers = {
    overview: overviewHandler,
    ytd: ytdHandler,
    funding: fundingHandler,
    payrolls: payrollsHandler,
    alerts: alertsHandler,
    paySchedules: paySchedulesHandler,
    downloadUrl: downloadUrlHandler,
    documentFile: documentFileHandler,
};

export default allDemoHandlers;
